import { useState, type ReactNode } from "react";
import { LockSimpleIcon } from "@phosphor-icons/react/LockSimple";
import { Icon } from "../../Icons/Icon";

/**
 * Click-feedback wrapper for parked / disabled-due-to-parking surfaces.
 *
 * Visually-disabled controls (dimmed, pointer-events-none) eat user clicks
 * silently, which leaves the operator wondering why nothing happened. This
 * wrapper sits one layer above the disabled target, catches the click that
 * pass through, and replays a "locked-door rattle" — the wrapped element
 * vibrates while a padlock pops in to its left, vibrates in unison, then
 * fades out + scales down.
 *
 * Used by:
 *   - `Button` when its `dedupeKey` matches an existing tray entry (the
 *     trigger button for a parked dialog).
 *   - `DialogSlideAsideChip_` when `disabledForGrouping` (a parked dialog
 *     belongs to a different route than the current one).
 *
 * Implementation: each click bumps a counter, and the two animated children
 * (the inner wrapper carrying `animate-parked-shake`, and the padlock
 * carrying `animate-parked-padlock`) use the counter as their React `key`
 * so they remount cleanly and the CSS animations replay from frame 0 —
 * the simplest way to re-trigger a one-shot keyframe in React.
 */
export function ParkedShakeWrapper_({ children }: { children: ReactNode }) {
  const [shakeKey, setShakeKey] = useState(0);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setShakeKey((k) => k + 1);
  }

  const spanLayout = "inline-block";

  // Keyboard interaction with a parked / disabled-for-grouping control is
  // impossible (the underlying element is `disabled` or `pointer-events-none`,
  // hence not focusable), so a keyboard equivalent for this shake feedback
  // is meaningless. oxlint's a11y rule is silenced for that reason.
  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <span className={`relative ${spanLayout}`} onClick={handleClick}>
      {shakeKey > 0 && (
        <span
          key={`padlock-${shakeKey}`}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-full mr-2 inline-flex -translate-y-1/2 animate-parked-padlock items-center justify-center text-red"
        >
          <Icon icon={LockSimpleIcon} size="md" weight="fill" />
        </span>
      )}
      <span
        key={`shake-${shakeKey}`}
        className={`${spanLayout} ${shakeKey > 0 ? "animate-parked-shake" : ""}`}
      >
        {children}
      </span>
    </span>
  );
}
